/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { en } from './en';
import { ar } from './ar';
import type { TranslationKey } from './messages';

export type Language = 'en' | 'ar';

interface I18nContextType {
  lang: Language;
  dir: 'ltr' | 'rtl';
  setLang: (lang: Language) => void;
  t: (key: TranslationKey) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const I18nProvider: React.FC<{ children: React.ReactNode; initialLang?: Language }> = ({
  children,
  initialLang = 'ar',
}) => {
  const [lang, setLangState] = useState<Language>(initialLang);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem('vye_lang') as Language;
    if (saved === 'en' || saved === 'ar') {
      const timer = setTimeout(() => {
        setLangState((prev) => (prev !== saved ? saved : prev));
      }, 0);
      return () => clearTimeout(timer);
    }
  }, []);

  const setLang = (newLang: Language) => {
    setLangState(newLang);
    if (typeof window !== 'undefined') {
      localStorage.setItem('vye_lang', newLang);
    }
  };

  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    // Update body direction for robust overriding
    document.body.style.direction = dir;
  }, [lang, dir]);

  const t = (key: TranslationKey): string => {
    const catalog = lang === 'en' ? en : ar;
    return catalog[key] || key;
  };

  return (
    <I18nContext.Provider value={{ lang, dir, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};
